/** Integration creation wizard — dynamic-length stepper over booking / fleet. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepButton,
  Stepper,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import { useSnackbar } from '@/components/Snackbar';
import { HelpLink } from '@/components/HelpLink';

import { CATALOG_STALE_MS, SCHEMA_STALE_MS } from './cachePolicy';
import { ResendEmailDialog } from './WebhookPanel';
import { Step1Kind } from './wizard/Step1Kind';
import { Step2System } from './wizard/Step2System';
import { Step3Account } from './wizard/Step3Account';
import { Step4BookingParams } from './wizard/Step4BookingParams';
import { Step4FleetSettings } from './wizard/Step4FleetSettings';
import { Step5Webhook } from './wizard/Step5Webhook';
import {
  buildCreateInput,
  WIZARD_DEFAULTS,
  type BookingWizardData,
  type FleetWizardData,
  type IntegrationKind,
  type WizardData,
} from './wizard/types';

interface StepDef {
  key: string;
  label: string;
}

const BOOKING_STEPS: StepDef[] = [
  { key: 'kind', label: 'Kind' },
  { key: 'system', label: 'System' },
  { key: 'account', label: 'Account' },
  { key: 'params', label: 'Parameters' },
  { key: 'webhook', label: 'Webhooks' },
];
const FLEET_STEPS: StepDef[] = [
  { key: 'kind', label: 'Kind' },
  { key: 'system', label: 'System' },
  { key: 'fleet', label: 'Settings' },
];

interface Props {
  open: boolean;
  toId: number;
  toCode: string;
  onClose: () => void;
  onCreated: () => void;
}

export function IntegrationCreateDialog({ open, toId, toCode, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  const [data, setData] = useState<WizardData>(WIZARD_DEFAULTS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The created integration whose webhook-setup e-mail is open: the flow ends when it closes. */
  const [emailFor, setEmailFor] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: integrationsApi.listCatalog,
    staleTime: CATALOG_STALE_MS,
  });
  const catalog = catalogQuery.data?.results ?? [];
  const entry = catalog.find((e) => e.id === data.booking.restech);

  const schemaQuery = useQuery({
    queryKey: ['integrations', 'schema', data.booking.restech],
    queryFn: () => integrationsApi.getSchema(data.booking.restech as string),
    enabled: data.kind === 'booking' && !!data.booking.restech,
    staleTime: SCHEMA_STALE_MS,
  });

  const steps = data.kind === 'booking' ? BOOKING_STEPS : FLEET_STEPS;

  function setKind(kind: IntegrationKind) {
    setData((prev) => ({ ...prev, kind }));
    setActiveStep(0);
    setMaxVisited(0);
    setError(null);
  }
  function patchBooking(partial: Partial<BookingWizardData>) {
    setData((prev) => ({ ...prev, booking: { ...prev.booking, ...partial } }));
  }
  function patchFleet(partial: Partial<FleetWizardData>) {
    setData((prev) => ({ ...prev, fleet: { ...prev.fleet, ...partial } }));
  }

  function reset() {
    setActiveStep(0);
    setMaxVisited(0);
    setData(WIZARD_DEFAULTS);
    setError(null);
  }

  function handleClose() {
    if (submitting) return;
    reset();
    onClose();
  }

  const currentKey = steps[activeStep]?.key;

  function validate(key: string | undefined): string | null {
    if (data.kind === 'booking') {
      const b = data.booking;
      if (key === 'system' && !b.restech) return 'Select a booking system.';
      if (key === 'account' && b.account_mode === 'multi' && !b.slug.trim())
        return 'An account slug is required for a multi-account integration.';
      if (key === 'params') {
        if (!b.source_id) return 'Select an import source.';
        const req = (schemaQuery.data?.required ?? []).filter((k) => k !== 'source_id');
        for (const k of req) {
          if (!b.config[k]) return `${k} is required.`;
        }
      }
    } else {
      const f = data.fleet;
      if (key === 'fleet' && (!f.customer_id.trim() || !f.username.trim() || !f.password.trim()))
        return 'Customer ID, username and password are required.';
    }
    return null;
  }

  function handleNext() {
    const err = validate(currentKey);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setActiveStep((s) => {
      const next = s + 1;
      setMaxVisited((m) => Math.max(m, next));
      return next;
    });
  }

  function handleStepClick(index: number) {
    if (submitting || index > maxVisited) return;
    setError(null);
    setActiveStep(index);
  }

  async function handleSubmit() {
    const err = validate(currentKey);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const row = await integrationsApi.create(toId, buildCreateInput(data));

      // Post-create webhook step (non-fatal — the integration already exists).
      if (data.kind === 'booking' && entry && entry.webhook_setup_mode === 'api' && data.booking.installWebhook) {
        try {
          await integrationsApi.installWebhook(toId, row.id);
        } catch (whErr) {
          const m = whErr instanceof ConsoleApiError ? whErr.message : 'webhook step failed';
          snackbar.show(
            `Integration created, but the webhook step did not complete: ${m}`,
            'warning',
          );
        }
      }

      snackbar.show('Integration created.', 'success');
      void qc.invalidateQueries({ queryKey: ['integrations', 'rows', toId] });
      // The webhook-setup e-mail to the vendor's support team is never sent from here: it opens,
      // to be sent by an explicit click (or copied to send by hand), and the flow ends when it closes.
      if (data.kind === 'booking' && entry?.webhook_setup_mode === 'email' && data.booking.openEmail) {
        setEmailFor(row.id);
        return;
      }
      reset();
      onCreated();
      onClose();
    } catch (e: unknown) {
      if (e instanceof ConsoleApiError) {
        setError(
          e.status === 409
            ? (e.body?.detail ?? 'That account already exists.')
            : (e.body?.detail ?? e.message),
        );
      } else {
        setError('Creation failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleEmailClosed() {
    setEmailFor(null);
    reset();
    onCreated();
    onClose();
  }

  const isLastStep = activeStep === steps.length - 1;

  function renderStep() {
    switch (currentKey) {
      case 'kind':
        return <Step1Kind kind={data.kind} onKind={setKind} />;
      case 'system':
        return (
          <Step2System
            kind={data.kind}
            restech={data.booking.restech}
            onRestech={(r) => patchBooking({ restech: r })}
            catalog={catalog}
            loading={catalogQuery.isLoading}
          />
        );
      case 'account':
        return <Step3Account booking={data.booking} onBooking={patchBooking} />;
      case 'params':
        return (
          <Step4BookingParams
            toId={toId}
            booking={data.booking}
            onBooking={patchBooking}
            schema={schemaQuery.data}
            schemaLoading={schemaQuery.isLoading}
          />
        );
      case 'webhook':
        return (
          <Step5Webhook
            booking={data.booking}
            onBooking={patchBooking}
            entry={entry}
            toCode={toCode}
          />
        );
      case 'fleet':
        return <Step4FleetSettings fleet={data.fleet} onFleet={patchFleet} />;
      default:
        return null;
    }
  }

  return (
    <>
      <Dialog
        open={open && emailFor === null}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle>
          Add integration <HelpLink story="top-integrations" />
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} alternativeLabel nonLinear sx={{ mb: 3 }}>
            {steps.map((s, index) => (
              <Step key={s.key} completed={index < activeStep && index <= maxVisited}>
                <StepButton
                  onClick={() => handleStepClick(index)}
                  disabled={index > maxVisited || submitting}
                >
                  {s.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 220 }}>{renderStep()}</Box>

          {submitting && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2, alignItems: 'center' }}
            >
              <CircularProgress size={24} />
              Creating integration…
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            disabled={activeStep === 0 || submitting}
          >
            Back
          </Button>
          {isLastStep ? (
            <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create integration'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext} disabled={submitting}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {emailFor !== null && (
        <ResendEmailDialog
          toId={toId}
          integrationId={emailFor}
          title="Send webhook-setup email"
          onClose={handleEmailClosed}
        />
      )}
    </>
  );
}
